import { useLazyQuery } from '@apollo/client/react';
import { differenceInMinutes } from 'date-fns';
import { useEffect, useState } from 'react';

import {
  GetInsuranceCardsDocument,
  GetPatientCertificateDocument,
} from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';

const FILE_NAME = '公費受給者証';

const ONE_MINUTE = 60 * 1000;
const INTERVAL_MINUTES = ONE_MINUTE * 60;

export const useFetchInsuranceCards = (
  willFetchInsuranceCards: boolean,
  patientId: string | null,
) => {
  const [error, setError] = useState<Error | null>(null);
  const [loadedDate, setLoadedDate] = useState<Date | null>(null);
  const [abortedTimerId, setAbortedTimerId] = useState<number | null>(null);
  const [isTimeout, setIsTimeout] = useState(false);
  const [controller] = useState(new AbortController());

  const [getInsuranceCards, { loading, data, error: insuranceCardsError }] = useLazyQuery(
    GetInsuranceCardsDocument,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
    },
  );
  const [getCertificate, { data: certificateData }] = useLazyQuery(GetPatientCertificateDocument, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });
  const insuranceCards = getNode(data, 'Patient')?.pfPatient?.insuranceCards?.insuranceCards || [];
  const hasCertificate =
    (getNode(certificateData, 'Patient')?.patientAttachments.nodes || []).length > 0;

  useEffect(() => {
    if (insuranceCardsError) {
      setError(insuranceCardsError);
    }
  }, [insuranceCardsError]);

  useEffect(() => {
    if (!data) return;

    setLoadedDate(new Date());
    setAbortedTimerId((timerId) => {
      if (timerId !== null) {
        clearTimeout(timerId);
      }
      return timerId;
    });
  }, [data]);

  useEffect(() => {
    if (willFetchInsuranceCards && patientId) {
      setError(null);

      getInsuranceCards({
        variables: { patientId },
        context: { fetchOptions: { signal: controller.signal } },
      });
      getCertificate({
        variables: {
          patientId,
          name: FILE_NAME,
        },
      });
    }
  }, [getCertificate, getInsuranceCards, willFetchInsuranceCards, patientId, controller.signal]);

  useEffect(() => {
    if (willFetchInsuranceCards && patientId) {
      // 保険証URLに有効期限（１日）があるので念のため初回ロードからINTERVAL_MINUTESたってたら再取得する
      if (!loadedDate || differenceInMinutes(new Date(), loadedDate) > INTERVAL_MINUTES) {
        setError(null);
        getInsuranceCards({
          variables: { patientId },
          context: { fetchOptions: { signal: controller.signal } },
        });
      }
    }
  }, [getInsuranceCards, willFetchInsuranceCards, loadedDate, patientId, controller.signal]);

  useEffect(() => {
    const _timerId = window.setTimeout(() => {
      controller.abort();
      setIsTimeout(true);
    }, ONE_MINUTE);
    setAbortedTimerId(_timerId);

    return () => {
      if (abortedTimerId !== null) {
        clearTimeout(abortedTimerId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    loading,
    timeout: isTimeout,
    hasCertificate,
    error,
    insuranceCards,
  };
};
